import { Head } from "@inertiajs/react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import AuthLayout from "@/components/tuw/auth/AuthLayout"
import { CsrfField, PasswordField, TextField } from "@/components/tuw/auth/fields"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { ENROLLMENT_CONFIRMATION } from "@/lib/openHouse/enrollmentWording"
import { CONFIRMATION } from "@/lib/openHouse/wording"

// The one sign in page for every user type (customers, freelancers, admins and
// the document-editor login), P11-H1. It posts as an ordinary browser form to
// `action` (Devise's sessions path, or the editor SSO path) so Devise's own
// redirects, the `redirect` hand-off and the per-role landing keep working.
// Field names are the Devise ones (`user[email]`, `user[password]`,
// `user[remember_me]`) that every existing browser test drives.
//
// `failure` is Devise's reason when this render is the re-render after a
// refused sign in ("invalid" covers a wrong password and an unknown email, so
// the banner never says which); any other alert renders as the server wrote it.
// "open_house_pending" is an Open House account waiting for email confirmation
// whose password was right (Key turn, 6f-d): it is led to confirm instead.
// "account_pending" is the same for an account-only enrollment, which has no project.
export default function SignIn({
  action,
  email = "",
  redirect = null,
  failure = null,
  flash = {},
  links,
}) {
  const badCredentials = failure === "invalid"
  const forgotWithEmail = email ? `${links.forgot}?email=${encodeURIComponent(email)}` : links.forgot

  return (
    <AuthLayout panel="signin" swap={{ prompt: "New here?", label: "Create an account", href: links.signUp }}>
      <Head title="Sign in · The Urban Writers" />

      <div>
        <h1 className="font-display text-[27px] font-bold leading-[1.15] tracking-[-0.6px] max-[560px]:text-[24px]">
          Welcome back
        </h1>
        <p className="mt-2 text-[14px] leading-[1.55] text-muted-foreground">
          Sign in to pick up where you left off.
        </p>
      </div>

      {flash.notice && <AuthAlert tone="good">{flash.notice}</AuthAlert>}
      {failure === "open_house_pending" && (
        <AuthAlert tone="quiet">
          {CONFIRMATION.signInBeforeConfirm} {CONFIRMATION.linkHelp}
        </AuthAlert>
      )}
      {failure === "account_pending" && (
        <AuthAlert tone="quiet">
          {ENROLLMENT_CONFIRMATION.requiredBeforeSignIn} {ENROLLMENT_CONFIRMATION.linkHelp}
        </AuthAlert>
      )}
      {badCredentials ? (
        <AuthAlert tone="bad">
          <b>That email and password didn’t match.</b> Check them and try again, or{" "}
          <a href={forgotWithEmail}>reset your password</a>.
        </AuthAlert>
      ) : (
        flash.alert && <AuthAlert tone="bad">{flash.alert}</AuthAlert>
      )}

      <form method="post" action={action} className="flex flex-col gap-4">
        <CsrfField />
        {redirect && <input type="hidden" name="redirect" value={redirect} />}

        <TextField
          id="user_email"
          name="user[email]"
          type="email"
          label="Email"
          placeholder="you@example.com"
          autoComplete="email"
          defaultValue={email}
          autoFocus
          required
        />

        <PasswordField
          id="user_password"
          name="user[password]"
          label="Password"
          placeholder="Your password"
          autoComplete="current-password"
          required
          extra={
            <a
              href={links.forgot}
              className="text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
            >
              Forgot password?
            </a>
          }
        />

        <div className="flex items-start gap-[11px]">
          {/* Unchecked boxes post nothing; the hidden 0 is what Rails reads then. */}
          <input type="hidden" name="user[remember_me]" value="0" />
          <Checkbox
            id="user_remember_me"
            name="user[remember_me]"
            value="1"
            defaultChecked
            className="mt-[1px] size-5 rounded-[6px] border-[1.5px] border-line-2 data-[state=checked]:border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background"
          />
          <Label htmlFor="user_remember_me" className="text-[13px] font-medium leading-[1.45]">
            Keep me signed in
          </Label>
        </div>

        <Button
          type="submit"
          className="mt-[6px] h-auto w-full rounded-[11px] px-4 py-3 text-[14px] font-semibold"
        >
          Sign in
        </Button>
      </form>

      <p className="text-center text-[13.5px] text-muted-foreground">
        New to The Urban Writers?{" "}
        <a
          href={links.signUp}
          className="font-bold text-foreground underline decoration-line-2 underline-offset-[3px] hover:decoration-foreground"
        >
          Create an account
        </a>
      </p>
      <p className="text-center text-[12.5px] text-ink-3">
        Freelancer?{" "}
        <a href={links.apply} className="font-semibold text-foreground underline decoration-line-2 underline-offset-[3px] hover:decoration-foreground">
          Apply here
        </a>
      </p>
    </AuthLayout>
  )
}
